import React, { useEffect, useRef, useState, useContext, useMemo, useCallback } from 'react';
import { renderToString } from 'react-dom/server';
import { Map, Popup } from 'react-map-gl';
import DeckGL, { ScatterplotLayer, PointCloudLayer, PathLayer } from 'deck.gl';
import PopupComponent from './Popup';
import './styles.scss';
import { notification } from 'antd';
import { SaveOutlined, UploadOutlined } from '@ant-design/icons';
import HydraulicSimulatorService from '../../domain/HydraulicSimulator/HydraulicSimulatorService';
import TreeFiles from './TreeFiles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mmsEventsService } from '../../events/mms.subject';
import 'mapbox-gl/dist/mapbox-gl.css';
import DashboardContext from '@/contexts/DashboardContext';
import { Button, Tooltip } from '@hydrodigital/design-system';

const hydraulicSimulatorService = new HydraulicSimulatorService();

const mapboxApiAccessToken = process.env.VITE_MAPBOX_ACCESS_TOKEN;
const initialStyle = {
	position: 'relative',
	width: '100%',
	height: '100%',
};

const initialViewState = {
	longitude: -74,
	latitude: 40,
	zoom: 11,
	pitch: 0,
	bearing: 0,
};

// Función para validar coordenadas
const isValidCoordinate = (lng, lat) => {
	return (
		typeof lng === 'number' && typeof lat === 'number' && lng >= -180 && lat >= -90 && lat <= 90
	);
};

// DeckGL handles performance optimization natively - no custom debounce needed

// Aggressive viewport culling - only render elements within tight viewport bounds
const getVisibleElements = (elements, viewport, padding = 0.05, mapRef = null) => {
	const { longitude, latitude, zoom } = viewport;
	let bounds;

	// Try multiple ways to access the map instance (same as isInViewport)
	let map = null;

	if (mapRef?.current) {
		// Method 1: Direct access to map instance (react-map-gl v7+)
		if (mapRef.current.getMap && typeof mapRef.current.getMap === 'function') {
			try {
				map = mapRef.current.getMap();
			} catch (e) {
				// Silent fail, try next method
			}
		}

		// Method 2: Direct map property access
		if (!map && mapRef.current._map) {
			try {
				map = mapRef.current._map;
			} catch (e) {
				// Silent fail, try next method
			}
		}

		// Method 3: Check if mapRef.current is already the map
		if (!map && mapRef.current.getBounds && typeof mapRef.current.getBounds === 'function') {
			try {
				map = mapRef.current;
			} catch (e) {
				// Silent fail
			}
		}
	}

	// If we have a map instance, get bounds
	if (map && typeof map.getBounds === 'function') {
		try {
			const mapBounds = map.getBounds();
			const currentBounds = {
				north: mapBounds.getNorth(),
				south: mapBounds.getSouth(),
				east: mapBounds.getEast(),
				west: mapBounds.getWest(),
			};

			// Apply padding to the actual visible bounds
			const latRange = currentBounds.north - currentBounds.south;
			const lngRange = currentBounds.east - currentBounds.west;

			bounds = {
				north: currentBounds.north + latRange * padding,
				south: currentBounds.south - latRange * padding,
				east: currentBounds.east + lngRange * padding,
				west: currentBounds.west - lngRange * padding,
			};
		} catch (e) {
			// Silent fail, use fallback
		}
	}

	if (!map) {
		// Fallback to calculated bounds
		const zoomFactor = Math.pow(2, zoom);
		const latRange = 360 / zoomFactor;
		const lngRange = 360 / zoomFactor;

		bounds = {
			north: latitude + latRange * (0.5 + padding),
			south: latitude - latRange * (0.5 + padding),
			east: longitude + lngRange * (0.5 + padding),
			west: longitude - lngRange * (0.5 + padding),
		};
	}

	const visibleElements = elements.filter((element) => {
		if (!element.longitude || !element.latitude) return false;

		return (
			element.latitude >= bounds.south &&
			element.latitude <= bounds.north &&
			element.longitude >= bounds.west &&
			element.longitude <= bounds.east
		);
	});

	return visibleElements;
};

// Progressive node visibility based on zoom and viewport
const getProgressiveNodes = (nodes, zoom, viewport, mapRef = null) => {
	const totalNodes = nodes.length;

	// Dynamic zoom thresholds based on dataset size
	let zoomThreshold;
	if (totalNodes < 5000) {
		zoomThreshold = 12.0; // Small datasets: show from zoom 12
	} else if (totalNodes >= 5000 && totalNodes < 10000) {
		zoomThreshold = 13.5; // Medium datasets: show from zoom 13.5
	} else {
		zoomThreshold = 15.7; // Large datasets: show from zoom 15.7
	}

	// No nodes until appropriate zoom threshold
	if (zoom <= zoomThreshold) {
		return [];
	}

	// SIMPLE LOGIC: Adjust padding based on dataset size AND zoom level
	if (totalNodes < 5000) {
		// Small datasets: Show ALL nodes regardless of viewport (performance is not an issue)
		return nodes;
	} else if (totalNodes < 10000) {
		// Medium datasets: Use moderate viewport culling
		return getVisibleElements(nodes, viewport, 0.3, mapRef);
	} else {
		// Large datasets: Use reasonable padding for viewport culling
		let padding;
		if (zoom >= 17) {
			padding = 0.5; // Very high zoom: tight viewport culling
		} else if (zoom >= 16) {
			padding = 0.3; // High zoom: conservative culling
		} else if (zoom >= 15.5) {
			padding = 0.2; // Medium-high zoom: minimal padding
		} else if (zoom >= 15) {
			padding = 0.1; // Medium zoom: very tight culling
		} else {
			padding = 0.05; // Low zoom: minimal padding
		}
		return getVisibleElements(nodes, viewport, padding, mapRef);
	}
};

// Lines rendering - NO CULLING, always show all lines at all zoom levels
const optimizeLinesForZoom = (lines, zoom, viewport, nodeMap, mapRef, dataCenter = null) => {
	// ALWAYS show ALL lines regardless of zoom or viewport - no culling applied
	return lines;
};

// Helper function to check if a coordinate point is in viewport
const isPointInViewport = (coord, viewport) => {
	const { longitude, latitude, zoom } = viewport;
	const zoomFactor = Math.pow(2, zoom);
	const latRange = 360 / zoomFactor;
	const lngRange = 360 / zoomFactor;

	return (
		coord[1] >= latitude - latRange * 1.0 &&
		coord[1] <= latitude + latRange * 1.0 &&
		coord[0] >= longitude - lngRange * 1.0 &&
		coord[0] <= longitude + lngRange * 1.0
	);
};

const isPointInViewportWithPadding = (
	coord,
	viewport,
	padding = 0.5,
	useDataCenter = false,
	dataCenter = null,
) => {
	let { longitude, latitude, zoom } = viewport;

	// Use data center instead of viewport center if requested
	if (useDataCenter && dataCenter) {
		latitude = dataCenter.latitude;
		longitude = dataCenter.longitude;
	}

	const zoomFactor = Math.pow(2, zoom);
	const latRange = 360 / zoomFactor;
	const lngRange = 360 / zoomFactor;

	return (
		coord[1] >= latitude - latRange * padding &&
		coord[1] <= latitude + latRange * padding &&
		coord[0] >= longitude - lngRange * padding &&
		coord[0] <= longitude + lngRange * padding
	);
};

// Calculate the geographic center of all data points
const calculateDataCenter = (nodes, lines) => {
	let minLat = Infinity,
		maxLat = -Infinity;
	let minLng = Infinity,
		maxLng = -Infinity;

	// Include all nodes
	nodes.forEach((node) => {
		if (node.latitude && node.longitude) {
			minLat = Math.min(minLat, node.latitude);
			maxLat = Math.max(maxLat, node.latitude);
			minLng = Math.min(minLng, node.longitude);
			maxLng = Math.max(maxLng, node.longitude);
		}
	});

	// Include all line coordinates
	lines.forEach((line) => {
		if (line.coordinates) {
			line.coordinates.forEach((coord) => {
				minLat = Math.min(minLat, coord[1]);
				maxLat = Math.max(maxLat, coord[1]);
				minLng = Math.min(minLng, coord[0]);
				maxLng = Math.max(maxLng, coord[0]);
			});
		}
	});

	// Return center point
	return {
		latitude: (minLat + maxLat) / 2,
		longitude: (minLng + maxLng) / 2,
	};
};

const isInViewport = (
	node,
	viewport,
	padding = 0.5,
	mapRef = null,
	useDataCenter = false,
	dataCenter = null,
) => {
	// Try multiple ways to access the map instance
	let map = null;

	if (mapRef?.current) {
		// Method 1: Direct access to map instance (react-map-gl v7+)
		if (mapRef.current.getMap && typeof mapRef.current.getMap === 'function') {
			try {
				map = mapRef.current.getMap();
			} catch (e) {
				// Silent fail, try next method
			}
		}

		// Method 2: Direct map property access
		if (!map && mapRef.current._map) {
			try {
				map = mapRef.current._map;
			} catch (e) {
				// Silent fail, try next method
			}
		}

		// Method 3: Check if mapRef.current is already the map
		if (!map && mapRef.current.getBounds && typeof mapRef.current.getBounds === 'function') {
			try {
				map = mapRef.current;
			} catch (e) {
				// Silent fail
			}
		}
	}

	// If we have a map instance, try to get bounds
	if (map && typeof map.getBounds === 'function') {
		try {
			const bounds = map.getBounds();
			const latRange = bounds.getNorth() - bounds.getSouth();
			const lngRange = bounds.getEast() - bounds.getWest();

			// Use real map bounds for viewport culling
			return (
				node.latitude >= bounds.getSouth() - latRange * padding &&
				node.latitude <= bounds.getNorth() + latRange * padding &&
				node.longitude >= bounds.getWest() - lngRange * padding &&
				node.longitude <= bounds.getEast() + lngRange * padding
			);
		} catch (e) {
			// Silent fail, use fallback
		}
	}

	// Fallback: Use viewport-based calculation
	let { longitude, latitude, zoom } = viewport;

	// Fallback calculation with generous padding
	const zoomFactor = Math.pow(2, zoom);
	const latRange = 360 / zoomFactor;
	const lngRange = 360 / zoomFactor;

	// Apply extra padding for fallback to compensate for less accurate bounds
	const fallbackPadding = padding * 2;

	return (
		node.latitude >= latitude - latRange * fallbackPadding &&
		node.latitude <= latitude + latRange * fallbackPadding &&
		node.longitude >= longitude - lngRange * fallbackPadding &&
		node.longitude <= longitude + lngRange * fallbackPadding
	);
};

const MmsClusteredView = () => {
	const organizationSettings = localStorage.getItem('organizationSettings');
	const mapStyle =
		JSON.parse(organizationSettings)?.mapSettings?.settings?.style ||
		'mapbox://styles/mapbox/light-v11';
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isViewMode, setIsViewMode] = useState(false);
	const [assetsData, setAssetsData] = useState([]);
	const [mmsId, setMmsId] = useState(null);
	const { value: context } = useContext(DashboardContext);

	const [allNodes, setAllNodes] = useState([]); // Store all nodes
	const [allLines, setAllLines] = useState([]); // Store all lines
	const [originalAssets, setOriginalAssets] = useState([]); // Store original assets for comparison

	// Draggable popup states
	const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const [popupActive, setPopupActive] = useState(false); // Track popup active state

	// Node dragging states
	const [draggedNode, setDraggedNode] = useState(null);
	const [isDraggingNode, setIsDraggingNode] = useState(false);

	// Calculate the geographic center of all data (memoized for performance)
	const dataCenter = useMemo(() => {
		if (allNodes.length === 0 && allLines.length === 0) return null;
		return calculateDataCenter(allNodes, allLines);
	}, [allNodes, allLines]);
	const mapRef = useRef(null);
	const [viewport, setViewport] = useState(initialViewState);
	const [popupInfo, setPopupInfo] = useState(null);
	const [selectedAsset, setSelectedAsset] = useState(null);
	const [fileName, setFileName] = useState(null);
	const refTreeData = useRef(null);
	const [isLoading, setIsLoading] = useState(false);

	// Performance optimization: Memoize node lookup with reduced dataset
	const nodeMap = useMemo(() => {
		const map = new window.Map();
		allNodes.forEach((node) => map.set(node.id, node));
		return map;
	}, [allNodes]);

	// Ultra performance optimization: Heavily optimized lines
	const visibleLines = useMemo(() => {
		if (allLines.length === 0) return [];

		return optimizeLinesForZoom(allLines, viewport.zoom, viewport, nodeMap, mapRef, dataCenter);
	}, [allLines, viewport.zoom, viewport.longitude, viewport.latitude, nodeMap, mapRef, dataCenter]);

	// Progressive node rendering based on zoom level
	const visibleNodes = useMemo(() => {
		if (allNodes.length === 0) return [];

		const totalNodes = allNodes.length;

		// Dynamic zoom thresholds based on dataset size
		let zoomThreshold;
		if (totalNodes < 5000) {
			zoomThreshold = 12.0; // Small datasets: show from zoom 12
		} else if (totalNodes >= 5000 && totalNodes < 10000) {
			zoomThreshold = 13.5; // Medium datasets: show from zoom 13.5
		} else {
			zoomThreshold = 15.7; // Large datasets: show from zoom 15.7
		}

		// NO NODES until appropriate zoom threshold (except edited nodes)
		if (viewport.zoom <= zoomThreshold) {
			// Only show edited nodes that are explicitly edited by user
			return allNodes.filter(
				(node) => node.edited && isInViewport(node, viewport, 0.5, mapRef, true, dataCenter),
			);
		}

		// Above zoom threshold: Apply progressive rendering with viewport filtering
		const progressiveNodes = getProgressiveNodes(allNodes, viewport.zoom, viewport, mapRef);

		// Always ensure edited nodes are visible regardless of zoom level
		const editedNodes = allNodes.filter(
			(node) => node.edited && isInViewport(node, viewport, 0.5, mapRef, true, dataCenter),
		);

		// Add nodes that are connected to visible lines (to avoid orphaned lines)
		// But only if we're showing nodes (not in large dataset low zoom mode)
		const connectedNodeIds = new Set();
		visibleLines.forEach((line) => {
			if (line.sourceNodeId) connectedNodeIds.add(line.sourceNodeId);
			if (line.targetNodeId) connectedNodeIds.add(line.targetNodeId);
		});

		const connectedNodes = allNodes.filter(
			(node) =>
				connectedNodeIds.has(node.id) &&
				!editedNodes.some((editedNode) => editedNode.id === node.id) &&
				!progressiveNodes.some((progNode) => progNode.id === node.id),
		);

		// Combine all node types, avoiding duplicates
		const editedNodeIds = new Set(editedNodes.map((node) => node.id));
		const progressiveNodeIds = new Set(progressiveNodes.map((node) => node.id));
		const regularNodes = progressiveNodes.filter((node) => !editedNodeIds.has(node.id));
		const finalConnectedNodes = connectedNodes.filter(
			(node) => !editedNodeIds.has(node.id) && !progressiveNodeIds.has(node.id),
		);

		const allCombinedNodes = [...editedNodes, ...regularNodes, ...finalConnectedNodes];

		// Apply simple viewport culling - for small datasets show ALL nodes
		let viewportFilteredNodes;
		if (totalNodes < 5000) {
			// Small datasets: Show ALL nodes (no viewport culling needed)
			viewportFilteredNodes = allCombinedNodes;
		} else {
			// Medium/Large datasets: Apply viewport culling with zoom-dependent padding
			let viewportPadding;
			if (totalNodes >= 10000) {
				// Large datasets: reasonable padding for proper viewport culling
				if (viewport.zoom >= 17) {
					viewportPadding = 0.5; // Very high zoom: tight viewport culling
				} else if (viewport.zoom >= 16) {
					viewportPadding = 0.3; // High zoom: conservative culling
				} else if (viewport.zoom >= 15.5) {
					viewportPadding = 0.2; // Medium-high zoom: minimal padding
				} else if (viewport.zoom >= 15) {
					viewportPadding = 0.1; // Medium zoom: very tight culling
				} else {
					viewportPadding = 0.05; // Low zoom: minimal padding
				}
			} else {
				viewportPadding = 0.8; // Medium datasets: moderate culling
			}
			viewportFilteredNodes = allCombinedNodes.filter(
				(node) =>
					isInViewport(node, viewport, viewportPadding, mapRef, true, dataCenter) || node.edited,
			);
		}

		return viewportFilteredNodes;
	}, [
		allNodes,
		visibleLines,
		viewport.zoom,
		viewport.longitude,
		viewport.latitude,
		mapRef,
		dataCenter,
	]);

	// Infrastructure color mapping based on type
	const getInfrastructureColor = useCallback((infraType) => {
		const type = infraType?.toLowerCase();

		// Node types (based on GeoJSON reference) - Purple
		if (['junctions', 'junction', 'outfalls', 'outfall', 'storage'].includes(type)) {
			return '#023e65'; // Purple for nodes
		}

		// Line types (based on GeoJSON reference) - Blue
		if (['conduits', 'conduit', 'weirs', 'weir', 'pipes', 'pipe'].includes(type)) {
			return '#0077FF'; // Blue for lines
		}

		// Special cases
		if (['pumps', 'pump'].includes(type)) {
			return '#FF6B35'; // Orange for pumps
		}

		// Default fallback
		return '#0077FF';
	}, []);

	// Infrastructure thickness mapping based on type
	const getInfrastructureThickness = useCallback((infraType) => {
		switch (infraType?.toLowerCase()) {
			case 'conduits':
				return 2;
			case 'weirs':
				return 3; // Thicker for weirs
			case 'pumps':
				return 4; // Thickest for pumps
			default:
				return 2;
		}
	}, []);

	const getNodeSize = useCallback(
		(zoom) => {
			// Dynamic size based on dataset size and zoom level
			const totalNodes = allNodes.length;

			// For large datasets, use smaller nodes
			if (totalNodes >= 10000) {
				// Very large datasets: extra small nodes
				return zoom > 18 ? 3 : zoom > 16 ? 2.5 : zoom > 14 ? 2 : zoom > 12 ? 1.5 : 1;
			} else if (totalNodes >= 5000) {
				// Large datasets: small nodes
				return zoom > 18 ? 4 : zoom > 16 ? 3.5 : zoom > 14 ? 3 : zoom > 12 ? 2.5 : 1.5;
			} else {
				// Normal/small datasets: regular small nodes
				return zoom >= 18.5
					? 9
					: zoom > 18
						? 6
						: zoom > 16
							? 4
							: zoom > 14
								? 3
								: zoom > 12.5
									? 2.5
									: 2;
			}
		},
		[allNodes.length],
	);

	function getLinkColorFromType(type, invisible, edited) {
		if (invisible) return '#f05775';
		if (edited) return '#79f865';
		switch (type?.toUpperCase()) {
			case 'PIPE':
				return '#1e96be';
			case 'PUMPS':
				return '#ff991f';
			case 'ORIFICES':
				return '#720c9d';
			case 'WEIRS':
				return '#720c9d';
			case 'CONDUITS':
				return '#1e96be';
			default:
				return '#1e96be';
		}
	}

	const handleNodeClick = useCallback(
		(node) => {
			// Only allow clicks at zoom >= 13.6
			if (viewport.zoom < 13.6) return;

			// Find the original asset from assetsData.assets using the assetId
			const originalAsset = assetsData.assets?.find((asset) => asset.assetId === node.assetId);
			if (originalAsset) {
				setSelectedAsset(originalAsset);
				setPopupInfo({
					info: originalAsset,
					longitude: node.longitude,
					latitude: node.latitude,
				});
			}
		},
		[assetsData.assets, viewport.zoom],
	);

	// DeckGL Layers - High Performance GPU Rendering
	const nodesLayer = useMemo(() => {
		if (visibleNodes.length === 0) return null;

		return new ScatterplotLayer({
			id: 'nodes-layer',
			data: visibleNodes.filter((node) => isValidCoordinate(node.longitude, node.latitude)),
			pickable: true,
			opacity: viewport.zoom > 10 ? 0.9 : 0.7,
			// stroked: true,
			filled: true,
			radiusScale: 1,
			radiusMinPixels: getNodeSize(viewport.zoom),
			radiusMaxPixels: getNodeSize(viewport.zoom) * 2,
			lineWidthMinPixels: 0.5,
			getPosition: (d) => [d.longitude, d.latitude],
			getRadius: (d) => getNodeSize(viewport.zoom),
			getFillColor: (d) => {
				const color = getInfrastructureColor(d.type) || '#023e65';
				// Convert hex to RGB array
				const hex = color.replace('#', '');
				return [
					parseInt(hex.substr(0, 2), 16),
					parseInt(hex.substr(2, 2), 16),
					parseInt(hex.substr(4, 2), 16),
					255,
				];
			},
			getLineColor: [255, 255, 255, 200], // White stroke
			onClick: (info) => {
				if (viewport.zoom >= 13.6 && info.object && !isDraggingNode) {
					handleNodeClick(info.object);
				}
			},
			// Enable dragging for nodes
			autoHighlight: true,
			highlightColor: [255, 255, 0, 100], // Yellow highlight when hovering
		});
	}, [
		visibleNodes,
		viewport.zoom,
		getInfrastructureColor,
		getNodeSize,
		handleNodeClick,
		isDraggingNode,
	]);

	const linesLayer = useMemo(() => {
		if (visibleLines.length === 0) return null;

		// Convert lines to path format for DeckGL
		const pathData = visibleLines
			.map((line) => {
				const sourceNode = nodeMap.get(line.sourceNodeId);
				const targetNode = nodeMap.get(line.targetNodeId);

				if (sourceNode && targetNode) {
					return {
						...line,
						path: [
							[sourceNode.longitude, sourceNode.latitude],
							[targetNode.longitude, targetNode.latitude],
						],
					};
				}
				return null;
			})
			.filter(Boolean);

		return new PathLayer({
			id: 'lines-layer',
			data: pathData,
			pickable: true,
			widthScale: 0.1,
			widthMinPixels:
				viewport.zoom > 18
					? 8
					: viewport.zoom > 16
						? 6
						: viewport.zoom > 14
							? 4
							: viewport.zoom > 12
								? 3
								: 2,
			getPath: (d) => d.path,
			getColor: (d) => {
				const color = getLinkColorFromType(d.type, d.invisible, d.edited);
				const hex = color.replace('#', '');
				return [
					parseInt(hex.substr(0, 2), 16),
					parseInt(hex.substr(2, 2), 16),
					parseInt(hex.substr(4, 2), 16),
					viewport.zoom > 10 ? 200 : 150,
				];
			},
			getWidth: (d) => getInfrastructureThickness(d.type) || 1,
			onClick: (info) => {
				if (viewport.zoom >= 13.6 && info.object) {
					const selectedLine = allLines.find((line) => line.id === info.object.id);
					if (selectedLine) {
						// Find the original asset from assetsData.assets using the assetId
						const originalAsset = assetsData.assets?.find(
							(asset) => asset.assetId === selectedLine.assetId,
						);
						if (originalAsset) {
							setSelectedAsset(originalAsset);
							setPopupInfo({
								longitude: info.coordinate[0],
								latitude: info.coordinate[1],
								info: originalAsset,
							});
						}
					}
				}
			},
		});
	}, [
		visibleLines,
		nodeMap,
		viewport.zoom,
		getLinkColorFromType,
		getInfrastructureThickness,
		allLines,
		assetsData.assets,
	]);

	// DeckGL PathLayer handles line rendering directly - no GeoJSON conversion needed

	const getModelData = useCallback(
		async (mmsId, filename) => {
			setIsLoading(true);
			// Clear existing data before loading new data
			setAllNodes([]);
			setAllLines([]);
			setSelectedAsset(null);
			setPopupInfo(null);

			try {
				const response = await hydraulicSimulatorService.getAssetsFromMmsid(
					mmsId,
					context.selectedLocation,
				);

				setAssetsData(response);
				// Store original assets for coordinate comparison
				setOriginalAssets(JSON.parse(JSON.stringify(response.assets)));
				parseMapData(response);
				const midPoint = findMidpoint(response.assets);
				setViewport((prev) => ({
					...prev,
					longitude: midPoint[0],
					latitude: midPoint[1],
					zoom: 11,
				}));
				setMmsId(mmsId);
				setFileName(filename);
			} finally {
				setIsLoading(false);
			}
		},
		[context.selectedLocation],
	);

	// Function to count asset types in visible nodes
	// const getAssetTypeCounts = useMemo(() => {
	// 	const totalNodesInData = allNodes.length;
	// 	const counts = {
	// 		NODES: visibleNodes.length,
	// 		LINES: visibleLines.length,
	// 		ZOOM: Math.round(viewport.zoom * 10) / 10, // Round to 1 decimal
	// 		TOTAL: totalNodesInData,
	// 	};

	// 	// Show dataset size indicator
	// 	if (totalNodesInData >= 10000) {
	// 		counts.DATASET = 'VERY LARGE';
	// 		if (viewport.zoom <= 15.3) {
	// 			counts.STATUS = 'HIDDEN';
	// 		} else {
	// 			counts.STATUS = 'FILTERED';
	// 		}
	// 	} else if (totalNodesInData >= 5000) {
	// 		counts.DATASET = 'LARGE';
	// 		if (viewport.zoom <= 15) {
	// 			counts.STATUS = 'HIDDEN';
	// 		} else {
	// 			counts.STATUS = 'FILTERED';
	// 		}
	// 	} else {
	// 		counts.DATASET = 'NORMAL';
	// 	}

	// 	visibleNodes.forEach((node) => {
	// 		const type = node.type || 'UNKNOWN';
	// 		counts[type] = (counts[type] || 0) + 1;
	// 	});

	// 	// Calculate dynamic zoom threshold for display (reuse existing totalNodesInData)
	// 	let zoomThreshold;
	// 	if (totalNodesInData < 5000) {
	// 		zoomThreshold = 12.0;
	// 	} else if (totalNodesInData >= 5000 && totalNodesInData < 10000) {
	// 		zoomThreshold = 13.5;
	// 	} else {
	// 		zoomThreshold = 15.7;
	// 	}

	// 	// Show coverage information
	// 	if (viewport.zoom > zoomThreshold) {
	// 		const coverage =
	// 			totalNodesInData > 0 ? Math.round((visibleNodes.length / totalNodesInData) * 100) : 0;
	// 		counts.COVERAGE = `${coverage}%`;
	// 	} else {
	// 		// Below zoom threshold
	// 		counts.STATUS = `NODES HIDDEN (zoom > ${zoomThreshold} to show)`;
	// 		counts.THRESHOLD = `ZOOM: ${viewport.zoom.toFixed(1)} / ${(zoomThreshold + 0.1).toFixed(1)}`;
	// 	}

	// 	return counts;
	// }, [visibleNodes, visibleLines, viewport.zoom, allNodes.length]);

	// Helper function to determine if infrastructure should be rendered as Point or Line
	const isPointInfrastructure = (infraType) => {
		const pointTypes = ['junctions', 'outfalls', 'storage', 'junction', 'outfall'];
		return pointTypes.includes(infraType?.toLowerCase());
	};

	const isLineInfrastructure = (infraType) => {
		const lineTypes = ['weirs', 'conduits', 'weir', 'conduit', 'pipes', 'pipe'];
		return lineTypes.includes(infraType?.toLowerCase());
	};

	// Parse legacy data with infrastructure type classification
	const parseMapData = useCallback(
		(resData) => {
			const data = resData.assets;
			const newNodes = [];
			const newLines = [];

			data.forEach((asset) => {
				// Use infrastructure type to determine if it should be a node or line
				const shouldBeNode =
					asset.assetType === 'NODE' ||
					isPointInfrastructure(asset.infrastructureType || asset.type);
				const shouldBeLink =
					asset.assetType === 'LINK' ||
					isLineInfrastructure(asset.infrastructureType || asset.type);

				if (shouldBeNode && asset.coordinates && asset.coordinates.length > 0) {
					const [lng, lat] = asset.coordinates[0];
					if (isValidCoordinate(lng, lat)) {
						newNodes.push({
							...asset,
							id: asset.assetId,
							longitude: lng,
							latitude: lat,
							label: asset.assetId,
							type: asset.infrastructureType || asset.type || 'junction',
						});
					}
				} else if (shouldBeLink) {
					newLines.push({
						...asset,
						id: asset.assetId,
						sourceNodeId: asset.node1,
						targetNodeId: asset.node2,
						color: getInfrastructureColor(asset.infrastructureType || asset.type) || '#888',
						thickness: getInfrastructureThickness(asset.infrastructureType || asset.type) || 2,
						type: asset.infrastructureType || asset.type || 'conduit',
					});
				}
			});

			console.log(`📊 Loaded: ${newNodes.length} nodes, ${newLines.length} lines`);
			setAllNodes(newNodes);
			setAllLines(newLines);
		},
		[getInfrastructureColor, getInfrastructureThickness],
	);

	// function getNodeShape(node) {
	// 	// Progressive node size based on zoom level - slightly larger than before
	// 	const baseSize = getNodeSize(viewport.zoom);
	// 	const styles = {
	// 		outfall: {
	// 			width: `${baseSize}px`,
	// 			height: `${baseSize}px`,
	// 			backgroundColor: '#8B0000',
	// 			borderRadius: '50%',
	// 			display: 'inline-block',
	// 			opacity: viewport.zoom < 12 ? 0.8 : 1,
	// 		},
	// 		rainGauges: {
	// 			width: `${baseSize + 4}px`,
	// 			height: `${baseSize + 4}px`,
	// 			border: `2px solid #321a97`,
	// 			borderRadius: '50%',
	// 			backgroundColor: 'transparent',
	// 			display: 'inline-block',
	// 			opacity: viewport.zoom < 12 ? 0.8 : 1,
	// 		},
	// 		storage: {
	// 			width: `${baseSize}px`,
	// 			height: `${baseSize}px`,
	// 			backgroundColor: '#321a97',
	// 			borderRadius: '50%',
	// 			display: 'inline-block',
	// 			opacity: viewport.zoom < 12 ? 0.8 : 1,
	// 		},
	// 	};

	// 	// Handle both legacy and new GeoJSON types
	// 	const nodeType = node.type?.toLowerCase() || node.infrastructureType?.toLowerCase();

	// 	switch (nodeType) {
	// 		case 'outlets':
	// 		case 'outfalls':
	// 			return <div style={styles.outfall}></div>;
	// 		case 'rain gauges':
	// 			return <div style={styles.rainGauges}></div>;
	// 		case 'storage':
	// 			return <div style={{ ...styles.storage, backgroundColor: '#8E44AD' }}></div>;
	// 		case 'pumps':
	// 			return <div style={{ ...styles.storage, backgroundColor: '#ff991f' }}></div>;
	// 		case 'junctions':
	// 		default:
	// 			return <div style={{ ...styles.storage, backgroundColor: '#8E44AD' }}></div>;
	// 	}
	// }

	function findMidpoint(data) {
		let totalX = 0;
		let totalY = 0;
		let nodeCount = 0;

		data.forEach((item) => {
			if (item.assetType === 'NODE' && item.coordinates && item.coordinates.length > 0) {
				const [x, y] = item.coordinates[0];
				totalX += x;
				totalY += y;
				nodeCount++;
			}
		});

		if (nodeCount > 0) {
			const averageX = totalX / nodeCount;
			const averageY = totalY / nodeCount;
			return [averageX, averageY];
		} else {
			return null;
		}
	}

	const saveChanges = useCallback(async () => {
		// Helper function to check if coordinates changed
		const coordinatesChanged = (asset) => {
			const original = originalAssets.find((orig) => orig.assetId === asset.assetId);
			if (!original || !original.coordinates || !asset.coordinates) return false;

			const origLng = original.coordinates[0][0];
			const origLat = original.coordinates[0][1];
			const currLng = asset.coordinates[0][0];
			const currLat = asset.coordinates[0][1];

			// Compare with tolerance for floating point precision
			const tolerance = 0.0000001;
			return Math.abs(origLng - currLng) > tolerance || Math.abs(origLat - currLat) > tolerance;
		};

		// Helper function to check if attributes changed
		const attributesChanged = (asset) => {
			const original = originalAssets.find((orig) => orig.assetId === asset.assetId);
			if (!original || !original.attributes || !asset.attributes) {
				console.log(`⚠️ Asset ${asset.assetId} - Missing original or attributes`);
				return false;
			}

			console.log(`🔍 Checking asset ${asset.assetId}:`);
			console.log('  Original attributes:', original.attributes);
			console.log('  Current attributes:', asset.attributes);

			// Check if any attribute value changed
			const hasChanges = asset.attributes.some((attr) => {
				const originalAttr = original.attributes.find((origAttr) => origAttr.name === attr.name);
				if (!originalAttr) {
					console.log(`  ⚠️ Attribute "${attr.name}" not found in original`);
					return false;
				}
				// Compare values as strings to handle different types
				const changed = originalAttr.value.toString() !== attr.value.toString();
				if (changed) {
					console.log(
						`  ✅ Attribute changed - ${attr.name}: "${originalAttr.value}" → "${attr.value}"`,
					);
				}
				return changed;
			});

			console.log(`📊 Asset ${asset.assetId} - Attributes changed: ${hasChanges}`);
			return hasChanges;
		};

		// Filter assets: include invisible OR (edited AND (coordinates OR attributes changed))
		const dataToSend = assetsData.assets
			?.filter((asset) => {
				if (asset.invisible) return true;
				if (asset.edited) return true;
				return false;
			})
			.map((asset) => {
				const coordinates =
					asset.coordinates[0][1] && asset.coordinates[0][0] && asset.type !== 'conduits'
						? [
								{
									type: 'coordinates',
									name: 'x',
									value: asset.coordinates[0][0].toString(),
								},
								{
									type: 'coordinates',
									name: 'y',
									value: asset.coordinates[0][1].toString(),
								},
							]
						: [];
				return {
					assetId: asset.assetId,
					action: asset.edited ? 'UPDATE' : asset.invisible ? 'DELETE' : '',
					type: asset.type,
					attributeDTOList: [
						...coordinates,
						...(asset.attributes
							? asset.attributes
									.filter((at) => at.name !== 'name')
									.map((at) => ({
										name: at.name,
										value: at.value.toString(),
									}))
							: []),
					],
				};
			});

		// Check if there are any changes to save
		if (!dataToSend || dataToSend.length === 0) {
			notification.warning({
				message: 'No Changes',
				description: 'No coordinates or attributes were modified. Nothing to save.',
			});
			return;
		}

		console.log(`💾 Saving ${dataToSend.length} modified assets`);

		const response = await hydraulicSimulatorService.postSaveChangesModelFile(
			mmsId,
			dataToSend,
			context.selectedLocation,
		);

		if (response) {
			// Update URL params with new model_id
			setSearchParams({
				mmsId: response,
				fileName: fileName,
			});

			// Update local mmsId state
			setMmsId(response);

			// Trigger TreeFiles refresh to show new model as child
			mmsEventsService.setSubject({ type: 'handleGetTreeData' });

			// Re-fetch and refresh the map with new model_id
			await getModelData(response, fileName);

			// Close popup and clear selection
			setPopupInfo(null);
			setSelectedAsset(null);

			notification.success({
				message: 'Successful',
				description: `Model saved successfully. New version: ${response.substring(0, 8)}`,
			});
		} else {
			// Fallback to old behavior if no model_id returned
			setAssetsData((prev) => {
				const newData = {
					...prev,
					assets: prev.assets
						.map((asset) => ({ ...asset, edited: false }))
						.filter((asset) => !asset.invisible),
				};
				parseMapData(newData);
				return newData;
			});

			setSelectedAsset(null);
			notification.success({
				message: 'Successful',
				description: 'Model File Edited successfully',
			});
			mmsEventsService.setSubject({ type: 'handleGetTreeData' });
		}
	}, [
		assetsData.assets,
		mmsId,
		fileName,
		parseMapData,
		context.selectedLocation,
		setSearchParams,
		getModelData,
		originalAssets,
	]);

	const saveModel = useCallback(() => {
		navigate(`/upload/modelfiles?mmsId=${mmsId}&fileName=${fileName}`);
	}, [navigate, mmsId, fileName]);

	// Draggable popup handlers
	const handleMouseDown = useCallback(
		(e) => {
			setIsDragging(true);
			setDragStart({
				x: e.clientX - popupPosition.x,
				y: e.clientY - popupPosition.y,
			});
		},
		[popupPosition],
	);

	const handleMouseMove = useCallback(
		(e) => {
			if (isDragging) {
				setPopupPosition({
					x: e.clientX - dragStart.x,
					y: e.clientY - dragStart.y,
				});
			}
		},
		[isDragging, dragStart],
	);

	const handleMouseUp = useCallback(() => {
		setIsDragging(false);
	}, []);

	// Reset popup position when a new asset is selected
	useEffect(() => {
		if (popupInfo) {
			setPopupPosition({ x: 0, y: 0 });
		}
	}, [popupInfo]);

	// Node drag handlers for DeckGL
	const handleNodeDragStart = useCallback(
		(info) => {
			if (!info.object || viewport.zoom < 13.6) return;
			setIsDraggingNode(true);
			setDraggedNode(info.object);
		},
		[viewport.zoom],
	);

	const handleNodeDrag = useCallback(
		(info) => {
			if (!isDraggingNode || !draggedNode || !info.coordinate) return;

			const [lng, lat] = info.coordinate;
			if (!isValidCoordinate(lng, lat)) return;

			// Update node position in real-time
			setAllNodes((prevNodes) =>
				prevNodes.map((node) =>
					node.id === draggedNode.id
						? { ...node, longitude: lng, latitude: lat, edited: true }
						: node,
				),
			);
		},
		[isDraggingNode, draggedNode],
	);

	const handleNodeDragEnd = useCallback(
		(info) => {
			if (!isDraggingNode || !draggedNode || !info.coordinate) {
				setIsDraggingNode(false);
				setDraggedNode(null);
				return;
			}

			const [lng, lat] = info.coordinate;
			if (!isValidCoordinate(lng, lat)) {
				setIsDraggingNode(false);
				setDraggedNode(null);
				return;
			}

			// Update assetsData
			setAssetsData((prev) => {
				const copy = { ...prev };
				const index = copy.assets.findIndex((asset) => asset.assetId === draggedNode.id);
				if (index !== -1) {
					copy.assets[index].coordinates = [[lng, lat]];
					copy.assets[index].edited = true;
				}
				return copy;
			});

			// Update nodes and lines
			setAllNodes((prevNodes) => {
				const updatedNodes = prevNodes.map((node) =>
					node.id === draggedNode.id
						? { ...node, longitude: lng, latitude: lat, edited: true }
						: node,
				);

				// Update connected lines
				setAllLines((prevLines) =>
					prevLines.map((line) => {
						if (line.sourceNodeId === draggedNode.id) {
							const targetNode = updatedNodes.find((n) => n.id === line.targetNodeId);
							if (targetNode) {
								return {
									...line,
									coordinates: [
										[lng, lat],
										[targetNode.longitude, targetNode.latitude],
									],
								};
							}
						} else if (line.targetNodeId === draggedNode.id) {
							const sourceNode = updatedNodes.find((n) => n.id === line.sourceNodeId);
							if (sourceNode) {
								return {
									...line,
									coordinates: [
										[sourceNode.longitude, sourceNode.latitude],
										[lng, lat],
									],
								};
							}
						}
						return line;
					}),
				);

				return updatedNodes;
			});

			setIsDraggingNode(false);
			setDraggedNode(null);
		},
		[isDraggingNode, draggedNode],
	);

	// DeckGL handles mouse events directly through layer callbacks

	// DeckGL layers handle clicks directly - this is for clearing selection when clicking empty space
	const handleMapClick = useCallback(() => {
		// Clear selection when clicking on empty space
		// setPopupInfo(null);
		// setSelectedAsset(null);
		setPopupActive(false); // Deactivate popup on map click
	}, []);

	return (
		<div
			className="MapContainer"
			style={{
				height: '100vh',
				width: '100vw',
				position: 'relative',
				overflow: 'hidden',
				backgroundColor: '#ebebeb',
			}}
		>
			{isLoading && (
				<div
					style={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						zIndex: 1000,
						backgroundColor: 'rgba(0,0,0,0.8)',
						color: 'white',
						padding: '20px',
						borderRadius: '8px',
					}}
				>
					Loading map data...
				</div>
			)}

			{/* Performance indicator */}
			{/* <div
				style={{
					position: 'absolute',
					top: 10,
					left: 280,
					backgroundColor:
						getAssetTypeCounts.DATASET === 'VERY LARGE'
							? 'rgba(255,68,68,0.8)'
							: getAssetTypeCounts.DATASET === 'LARGE'
								? 'rgba(255,140,0,0.8)'
								: 'rgba(0,0,0,0.7)',
					color: 'white',
					padding: '5px 10px',
					borderRadius: '4px',
					fontSize: '12px',
					zIndex: 1000,
					border:
						getAssetTypeCounts.DATASET === 'VERY LARGE'
							? '1px solid #ff4444'
							: getAssetTypeCounts.DATASET === 'LARGE'
								? '1px solid orange'
								: 'none',
				}}
			>
				{getAssetTypeCounts.DATASET === 'VERY LARGE' && (
					<span style={{ fontWeight: 'bold', color: '#ffffff' }}>
						🔴 VERY LARGE DATASET ({getAssetTypeCounts.TOTAL} nodes) |
					</span>
				)}
				{getAssetTypeCounts.DATASET === 'LARGE' && (
					<span style={{ fontWeight: 'bold' }}>
						🔶 LARGE DATASET ({getAssetTypeCounts.TOTAL} nodes) |
					</span>
				)}
				{getAssetTypeCounts.STATUS === 'HIDDEN' && getAssetTypeCounts.DATASET === 'VERY LARGE' && (
					<span style={{ color: '#ffcc00' }}>NODES HIDDEN (zoom &gt; 15.3 to show) |</span>
				)}
				{getAssetTypeCounts.STATUS === 'HIDDEN' && getAssetTypeCounts.DATASET === 'LARGE' && (
					<span style={{ color: '#ffcc00' }}>NODES HIDDEN (zoom &gt; 15 to show) |</span>
				)}
				NODES: {getAssetTypeCounts.NODES} | LINES: {getAssetTypeCounts.LINES} |
				{getAssetTypeCounts.COVERAGE && `COVERAGE: ${getAssetTypeCounts.COVERAGE} | `}
				ZOOM: {viewport.zoom.toFixed(1)}
			</div> */}

			<Map
				ref={mapRef}
				initialViewState={initialViewState}
				{...viewport}
				mapboxAccessToken={mapboxApiAccessToken}
				mapStyle={mapStyle}
				style={initialStyle}
			>
				<DeckGL
					viewState={viewport}
					layers={[linesLayer, nodesLayer].filter(Boolean)}
					onViewStateChange={({ viewState }) => {
						setViewport(viewState);
					}}
					controller={{
						scrollZoom: { speed: 0.01, smooth: true },
						dragPan: !isDraggingNode, // Disable map panning when dragging a node
						dragRotate: false,
						doubleClickZoom: true,
						touchZoom: true,
						touchRotate: false,
						keyboard: true,
						inertia: true,
					}}
					getCursor={({ isHovering, isDragging }) =>
						isDragging ? 'grabbing' : isHovering ? 'pointer' : 'grab'
					}
					onClick={(info) => {
						handleMapClick();
					}}
					onDragStart={handleNodeDragStart}
					onDrag={handleNodeDrag}
					onDragEnd={handleNodeDragEnd}
					getTooltip={({ object, layer }) => {
						// Only show tooltips at zoom level where clicks are enabled
						if (!object || viewport.zoom < 13.6) return null;

						if (layer.id === 'nodes-layer') {
							return {
								html: `<div style="background: #232B36; color: white; padding: 8px; border-radius: 10px; font-size: 12px; pointer-events: none;">
									<span><strong>Name:</strong> ${object.label || object.assetId}</span><br/>
									<span><strong>Type:</strong> ${object.type}</span>
								</div>`,
								style: {
									backgroundColor: 'transparent',
									fontSize: '12px',
									zIndex: 99,
									pointerEvents: 'none',
								},
							};
						}
						if (layer.id === 'lines-layer') {
							return {
								html: `<div style="background: #232B36; color: white; padding: 8px; border-radius: 10px; font-size: 12px; pointer-events: none;">
									<span><strong>Name:</strong> ${object.id}</span><br/>
									<span><strong>Type:</strong> ${object.type}</span>
								</div>`,
								style: {
									backgroundColor: 'transparent',
									fontSize: '12px',
									zIndex: 99,
									pointerEvents: 'none',
								},
							};
						}
						return null;
					}}
				/>
				{/* Asset popup - Draggable */}
				{popupInfo && selectedAsset && (
					<Popup
						key={selectedAsset.assetId || selectedAsset.id}
						longitude={popupInfo.longitude}
						latitude={popupInfo.latitude}
						closeButton={false}
						closeOnClick={false}
						onClose={() => {
							setPopupActive(false);

							setPopupInfo(null);
							setSelectedAsset(null);
						}}
						anchor="top"
						offset={[popupPosition.x, popupPosition.y]}
						style={{
							zIndex: 2,
							maxWidth: '400px',
							minWidth: '200px',
						}}
					>
						<div
							onMouseMove={handleMouseMove}
							onMouseUp={handleMouseUp}
							onMouseLeave={handleMouseUp}
						>
							<PopupComponent
								onMouseDown={popupActive ? undefined : handleMouseDown}
								isDragging={isDragging}
								mmsId={assetsData.mmsId}
								object={selectedAsset}
								locationId={context.selectedLocation}
								isActive={popupActive}
								onClose={() => {
									setPopupActive(false);
									setPopupInfo(null);
									setSelectedAsset(null);
								}}
								onClick={(e) => {
									e.stopPropagation();
									setPopupActive(true);
								}}
								setAttributesValue={(response) => {
									setAssetsData((prev) => {
										const copy = { ...prev };
										const index = copy.assets.findIndex(
											(asset) => asset.assetId === selectedAsset.assetId,
										);
										if (index !== -1) {
											copy.assets[index].attributes = response;
											copy.assets[index].invisible = false;
											setSelectedAsset(copy.assets[index]);
											parseMapData(copy);
										}
										return copy;
									});
								}}
								onDelete={() => {
									setAssetsData((prev) => {
										const copy = { ...prev };
										const index = copy.assets.findIndex(
											(asset) => asset.assetId === selectedAsset.assetId,
										);
										if (index !== -1) {
											copy.assets[index].invisible = true;
											parseMapData(copy);
											setPopupInfo(null);
											setSelectedAsset(null);
										}
										return copy;
									});
								}}
								onRestore={() => {
									setAssetsData((prev) => {
										const copy = { ...prev };
										const index = copy.assets.findIndex(
											(asset) => asset.assetId === selectedAsset.assetId,
										);
										if (index !== -1) {
											copy.assets[index].invisible = false;
											parseMapData(copy);
											setPopupInfo(null);
											setSelectedAsset(null);
										}
										return copy;
									});
								}}
								onEdit={(asset) => {
									setShowLateralMenu(true);
								}}
								onUpdateAttributeValue={(key, newValue) => {
									setAssetsData((prev) => {
										const copy = { ...prev };
										const index = copy.assets.findIndex(
											(asset) => asset.assetId === selectedAsset.assetId,
										);
										if (index !== -1) {
											const attributeIndex = copy.assets[index].attributes.findIndex(
												(attr) => attr.name === key,
											);
											if (attributeIndex !== -1) {
												copy.assets[index].attributes[attributeIndex].value = newValue;
												copy.assets[index].edited = true;
												setSelectedAsset(copy.assets[index]);
												parseMapData(copy);
											}
										}
										return copy;
									});
									setPopupActive(false);
								}}
							/>
						</div>
					</Popup>
				)}
			</Map>

			{/* Action buttons */}
			<div className='absolute top-5 right-6 gap-3 flex' >
				<Tooltip text="Save changes" position="up" trigger="hover">
					<div>
						<Button
							variant='primary'
							label='Save Changes'
							showIcon={false}
							onClick={saveChanges}
							className='text-center'
							iconName='save'
						/>
					</div>
				</Tooltip>
				<Tooltip text="Upload model" position="up" trigger="hover">
					<div>
						<Button
							variant='primary'
							showIcon={false}
							label='Upload Model'
							onClick={saveModel}
							iconName='upload'
						/>
					</div>
				</Tooltip>
				
			</div>
			<TreeFiles ref={refTreeData} getModelData={getModelData} />
		</div>
	);
};

export default MmsClusteredView;
